'use client';

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export interface ColumnDef<T = any> {
  /** Column header label */
  header: string;
  /** Key to access the value from the row data, or a render function */
  accessorKey?: keyof T & string;
  /** Custom cell renderer — takes the row data and returns a ReactNode */
  cell?: (row: T) => React.ReactNode;
  /** If true, this column is hidden on mobile card view (e.g. long IDs) */
  hideOnMobile?: boolean;
  /** If true, this is rendered as the card's primary/title value on mobile */
  primary?: boolean;
  /** Custom className for the table cell */
  className?: string;
}

interface ResponsiveTableProps<T = any> {
  /** Column definitions */
  columns: ColumnDef<T>[];
  /** Row data array */
  data: T[];
  /** Optional row key accessor (defaults to 'id') */
  rowKey?: keyof T & string;
  /** Optional className for the row on desktop */
  rowClassName?: (row: T) => string;
  /** Optional action column renderer — shown at the end of each row/card */
  actions?: (row: T) => React.ReactNode;
  /** Optional empty state component */
  emptyState?: React.ReactNode;
}

function getCellValue<T>(row: T, col: ColumnDef<T>): React.ReactNode {
  if (col.cell) return col.cell(row);
  if (col.accessorKey) {
    const val = (row as any)[col.accessorKey];
    if (val === null || val === undefined) return '—';
    return String(val);
  }
  return '—';
}

export function ResponsiveTable<T extends Record<string, any>>({
  columns,
  data,
  rowKey = 'id' as keyof T & string,
  rowClassName,
  actions,
  emptyState,
}: ResponsiveTableProps<T>) {
  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <>
      {/* Desktop: Standard Table — hidden on small screens */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.header} className={col.className}>
                  {col.header}
                </TableHead>
              ))}
              {actions && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => (
              <TableRow
                key={String(row[rowKey])}
                className={rowClassName?.(row)}
              >
                {columns.map((col) => (
                  <TableCell key={col.header} className={col.className}>
                    {getCellValue(row, col)}
                  </TableCell>
                ))}
                {actions && (
                  <TableCell>{actions(row)}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: Stacked Cards — visible only on small screens */}
      <div className="md:hidden space-y-3">
        {data.map((row) => {
          const primaryCol = columns.find((c) => c.primary);
          const visibleCols = columns.filter((c) => !c.hideOnMobile && !c.primary);

          return (
            <Card
              key={String(row[rowKey])}
              className={`overflow-hidden ${rowClassName?.(row) || ''}`}
            >
              <CardContent className="p-4 space-y-3">
                {/* Primary value as card header */}
                {primaryCol && (
                  <div className="font-semibold text-sm">
                    {getCellValue(row, primaryCol)}
                  </div>
                )}

                {/* Key-value pairs */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {visibleCols.map((col) => (
                    <div key={col.header}>
                      <span className="text-muted-foreground text-xs font-medium block mb-0.5">
                        {col.header}
                      </span>
                      <div className="font-medium">{getCellValue(row, col)}</div>
                    </div>
                  ))}
                </div>

                {/* Actions row */}
                {actions && (
                  <div className="pt-2 border-t flex justify-end gap-2">
                    {actions(row)}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}
